import { Typography, Button, Tooltip, theme } from 'antd'
import { ArrowLeftOutlined, SearchOutlined, CloseOutlined, TeamOutlined, InfoCircleOutlined, ExportOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../Stores'
import { formatLastSeen } from '../../Helpers/formatDate'
import { formatParticipants } from '../../Helpers/participants'
import { CHAT_PRESENCE_ENABLED } from '../../Helpers/chatFeatures'
import ChatAvatar from '../../Components/common/ChatAvatar'
import OnlineIndicator from '../../Components/common/OnlineIndicator'

const { Text } = Typography

/**
 * Ключ ресурсу заявки в реєстрі ресурсів ядра (`APPLICATION_RESOURCE`):
 * картку відкриваємо за ключем — без імпорту контейнера заявок.
 */
const APPLICATION_RESOURCE_KEY = 'Transportation.Application'

/** Підказка кнопки «до сутності» за типом прив'язки. */
const BOUND_ENTITY_TOOLTIP: Record<string, string> = {
  task: 'Відкрити завдання',
  application: 'Відкрити заявку',
}

const ChatHeader = observer(() => {
  const { uiStore, chatStore, userStore } = useStore()
  const { token } = theme.useToken()

  const chat = chatStore.activeChat
  if (!chat) return null

  const displayName = chatStore.getChatDisplayName(chat)
  const avatarUrl = chatStore.getChatAvatar(chat)
  const isSearchOpen = uiStore.isMessageSearchOpen

  // Status line
  let statusText: string
  let isOnline = false

  if (chat.type === 'group') {
    statusText = formatParticipants(chat.participantIds.length)
  } else if (!CHAT_PRESENCE_ENABLED) {
    // Presence (online/lastSeen) is a phase-2 backend feature
    statusText = ''
  } else {
    const otherId = chat.participantIds.find((id: string) => id !== userStore.currentUserId)
    const otherUser = otherId ? userStore.getById(otherId) : undefined
    isOnline = otherUser?.status === 'online'
    statusText = isOnline ? 'у мережі' : otherUser ? formatLastSeen(otherUser.lastSeen) : ''
  }

  // «Друкує…» тимчасово витісняє статус (формулювання — у ChatStore.typingLabel)
  const typingLabel = chatStore.typingLabel(chat, true)
  const isTyping = typingLabel !== null
  if (typingLabel !== null) {
    statusText = typingLabel
  }

  const handleSearchToggle = () => {
    if (isSearchOpen) {
      uiStore.closeMessageSearch()
    } else {
      uiStore.openMessageSearch()
    }
  }

  // Головна відмінність entity-чатів: місток назад до сутності.
  // Dynamic import — TaskResource сам імпортує наш фасад openEntityChat,
  // статичний імпорт звідси замкнув би модульний цикл
  const openBoundEntity = async () => {
    if (!chat.entityUuid) return

    if (chat.entityType === 'application') {
      uiStore.sendChatBehindResources()

      const { OpenResourceCardTask } = await import('@/Core/Tasks/Resources/OpenResourceCardTask')

      void OpenResourceCardTask({
        resource: APPLICATION_RESOURCE_KEY,
        primaryFieldValue: chat.entityUuid,
        mode: 'view',
      })

      return
    }

    if (chat.entityType !== 'task') return

    // Віконна логіка: картка, відкрита З чату, лягає ПОВЕРХ чату;
    // клік по чату потім підніме його назад (див. ChatDrawer)
    uiStore.sendChatBehindResources()

    const [{ OpenResourceCardTask }, { TaskResource }] = await Promise.all([
      import('@/Core/Tasks/Resources/OpenResourceCardTask'),
      import('@/Containers/TaskSection/TaskContainer/Resources/TaskResource'),
    ])

    void OpenResourceCardTask({
      resource: TaskResource,
      primaryFieldValue: chat.entityUuid,
      mode: 'view',
    })
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        // На телефоні щільніше: назві чату сутності лишається місце на два рядки
        padding: uiStore.isMobile ? '8px 8px' : '10px 16px',
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
        gap: uiStore.isMobile ? 6 : 12,
      }}
    >
      {/* Back button — mobile only */}
      {uiStore.isMobile && (
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => uiStore.setActiveChatId(null)}
          style={{ marginRight: -4 }}
        />
      )}

      {/* Avatar */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <ChatAvatar
          name={displayName}
          src={avatarUrl}
          size={uiStore.isMobile ? 32 : 40}
          {...(chat.type === 'group' ? { icon: <TeamOutlined /> } : {})}
        />
        {isOnline && <OnlineIndicator />}
      </div>

      {/* Name + status */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Не більше двох рядків із трикрапкою: довга назва чату сутності
            («Заявка № … · клієнт») на телефоні інакше ламалася на чотири */}
        <div
          title={displayName}
          style={{
            fontSize: 15,
            fontWeight: 600,
            lineHeight: '20px',
            color: token.colorText,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
        >
          {displayName}
        </div>
        <Text
          type="secondary"
          style={{
            fontSize: 12,
            lineHeight: '16px',
            color: isTyping ? token.colorPrimary : isOnline ? 'var(--ant-color-success)' : undefined,
          }}
        >
          {statusText}
        </Text>
      </div>

      {/* Actions */}
      {chat.entityType && BOUND_ENTITY_TOOLTIP[chat.entityType] && chat.entityUuid && (
        <Tooltip title={BOUND_ENTITY_TOOLTIP[chat.entityType]}>
          <Button
            type="text"
            icon={<ExportOutlined />}
            onClick={() => void openBoundEntity()}
          />
        </Tooltip>
      )}
      <Tooltip title={isSearchOpen ? 'Закрити пошук' : 'Пошук повідомлень'}>
        <Button
          type="text"
          icon={isSearchOpen ? <CloseOutlined /> : <SearchOutlined />}
          onClick={handleSearchToggle}
        />
      </Tooltip>
      <Tooltip title="Про чат">
        <Button
          type="text"
          icon={<InfoCircleOutlined />}
          onClick={() => uiStore.toggleChatInfo()}
        />
      </Tooltip>
    </div>
  )
})

export default ChatHeader
